import React, { useCallback, useContext } from 'react';
import { LinkBlockView, LinkBlockViewProps } from './LinkBlock.view';
import { PostEditorContext } from '../states/PostEditorProvider';
import { PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { ActiveCustomizeIndexContext } from '../states/ActiveCustomizeIndexProvider';
import { hasLinksChecker, removeLink, updateLinkPartial } from '../TextEditor/helpers/withLinks';
import { GetLinkMetadataQuery, LinkGoogleCallToAction } from '../../../models/graphql';
import { CustomLink } from '../../../models/slate-editor';

export type LinkBlockControllerProps = {
  // props go here
};

export const LinkBlockController = (_props: LinkBlockControllerProps): JSX.Element => {
  const { state, dispatch, profiles: allProfiles, editors } = useContext(PostEditorContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const postGroup = state.postGroups[postGroupIndex];
  const { customizeContentPerProfile, posts, profiles: selectedProfilesValues } = postGroup;
  const link =
    customizeContentPerProfile && typeof customizeIndex === 'number'
      ? posts[customizeIndex].content.link
      : postGroup.groupPostAttributes.content.link;
  const text =
    typeof customizeIndex === 'number'
      ? postGroup.posts[customizeIndex].content.texts
      : postGroup.groupPostAttributes.content.texts;
  const selectedProfiles = selectedProfilesValues.length
    ? allProfiles.filter(p => selectedProfilesValues.map(sp => sp.profileId).includes(p.id))
    : null;
  const profiles =
    typeof customizeIndex === 'number' && selectedProfiles
      ? selectedProfiles.filter(p => p.id === postGroup.posts[customizeIndex].customizedProfileId)
      : selectedProfiles;
  const hasGoogleProfile = !!profiles?.some(p => p.__typename === 'GoogleSocialProfile');
  const googleLinkIsNone = hasGoogleProfile && !!link && link.googleCallToAction === LinkGoogleCallToAction.NONE;
  const [editorRefs] = editors;
  const hasLinkInText = text.some(t => !!hasLinksChecker(t.text) && !googleLinkIsNone);

  editorRefs.current[postGroupIndex].forEach(editor => {
    if (link) {
      const customLink: CustomLink = {
        ...link,
        children: [{ text: '' }],
        type: 'link'
      };
      editor.editor.setLink(customLink);
    }
  });

  const onRemove = () => {
    editorRefs.current[postGroupIndex].forEach(editor => {
      removeLink(editor.editor);
    });
    dispatch({
      type: 'linkRemove',
      payload: { customizeIndex, postGroupIndex, editors: editorRefs }
    });
  };

  const updateLinkMetadata = useCallback(
    (linkMetadata: GetLinkMetadataQuery['linkMetadata'], url: string) => {
      const matchedLink = linkMetadata.find(data => data.initialUrl === url);
      if (matchedLink) {
        editorRefs.current[postGroupIndex].forEach(editor => {
          updateLinkPartial(editor.editor, {
            linkMetadata: matchedLink,
            ...(matchedLink.isBitlink ? { useBitly: false, url: matchedLink.initialUrl } : {})
          });
        });
        dispatch({
          type: 'linkUpdateMetadata',
          payload: { linkMetadata: matchedLink, postGroupIndex, customizeIndex }
        });
      }
    },
    [editorRefs, dispatch, postGroupIndex, customizeIndex]
  );

  const viewProps: Omit<LinkBlockViewProps, 'link'> = {
    onRemove,
    hasLinkInText,
    profiles,
    updateLinkMetadata,
    editorRefs
  };
  return !!link ? <LinkBlockView {...viewProps} link={link} /> : <></>;
};
